import { Inspector } from './Inspector';
import {
  DEFAULT_CONTEXT,
  DEFAULT_GUARDS,
  DONE_TRANSITION_ACTION_NAME,
  ERROR_TRANSITION_ACTION_NAME,
  EVENTLESS_TRANSITION_ACTION_NAME,
  INITIAL_TRANSITION_ACTION_NAME,
} from './constants';
import {
  BaseStateNode,
  InternalEvent,
  MachineActionExec,
  MachineDefinition,
  MachineEvent,
  MachineGuard,
  MachineServiceNode,
  MachineState,
  MachineStateEventlessTransition,
  MachineStatus,
  MachineTransition,
  MachineTransitionData,
} from './types';
import {
  getActionByName,
  getEventName,
  getInitialState,
  getServiceByName,
  getTransitionActions,
  getTransitionCondition,
  getTransitionTarget,
  isDevelopment,
  isEventlessTransition,
  isInternalEvent,
  isServiceNode,
  makeArray,
} from './util';

export class StateMachine<
  TContext = any,
  TEvent extends { type: string } = any,
> {
  machineDefinition: MachineDefinition<TContext, TEvent>;
  initialState: MachineState;
  context: TContext;
  guards: Record<string, MachineGuard<TContext, TEvent>>;
  status: MachineStatus = 'initializing';
  inspector: Inspector<TEvent>;
  debug: boolean;

  constructor(
    machine: MachineDefinition<TContext, TEvent>,
    inspector: Inspector<TEvent>,
    debug = false
  ) {
    this.machineDefinition = machine;
    this.initialState = getInitialState(machine);
    this.context = machine.context || (DEFAULT_CONTEXT as TContext);
    this.guards = machine.guards || DEFAULT_GUARDS;
    this.inspector = inspector;
    this.debug = debug;
  }

  start() {
    if (this.isRunning()) {
      return;
    }

    this.startRunning();
  }

  implicitTransition(currentState?: MachineState) {
    const state =
      this.machineDefinition.states[currentState || this.initialState];
    if (isEventlessTransition(state)) {
      return this.transitionForEventless(currentState || this.initialState);
    }

    if (!isServiceNode(state)) {
      return;
    }

    return this.transitionForService(
      this.initialState,
      state as MachineServiceNode<TContext, TEvent>,
      { type: INITIAL_TRANSITION_ACTION_NAME },
      []
    );
  }

  stop() {
    this.stopRunning();
  }

  setContext(context: TContext) {
    this.context = context;
  }

  noop(state: MachineState, event: MachineEvent<TEvent>) {
    this.inspector.add({
      type: 'fsm.transition.noop',
      description: 'No transition change',
      event,
    });
    return { state, event, actions: [], context: this.context };
  }

  transition(
    currentState: MachineState,
    event: MachineEvent<TEvent>
  ): MachineTransitionData<TContext, TEvent> {
    const state = this.machineDefinition.states[currentState];
    const eventName = getEventName(event);

    if (!this.isRunning()) {
      throw new Error(
        `Machine is not running: Could not respond to ${eventName}, machine is "${this.status}"`
      );
    }

    if (!state && isDevelopment()) {
      throw new Error(
        `State "${currentState}" not found when responding to "${eventName}"`
      );
    }

    const topLevelTransitions = (this.machineDefinition.on || {}) as Record<
      string,
      | MachineTransition<TContext, TEvent>
      | MachineTransition<TContext, TEvent>[]
    >;
    const transitionNode = ((state as BaseStateNode<TContext, TEvent>)?.on ||
      {}) as Record<
      string,
      | MachineTransition<TContext, TEvent>
      | MachineTransition<TContext, TEvent>[]
    >;
    const transitions =
      transitionNode[eventName] || topLevelTransitions[eventName];

    if (!transitions) {
      this.inspector.add({
        type: 'fsm.event.ignore',
        description: `No transition for event: ${eventName} in state: ${currentState}`,
        event,
      });

      if (isDevelopment()) {
        throw new Error(
          `No transition for event: ${eventName} in state: ${currentState}`
        );
      } else {
        return this.noop(currentState, event);
      }
    }

    const allowedTransitions = makeArray(transitions).filter((t) => {
      return this.transitionGuardCheck(t, this.context, event);
    });

    if (allowedTransitions.length === 0) {
      this.inspector.add({
        type: 'fsm.transition.skip',
        description: 'Current state has no allowed transitions',
        event,
      });

      return this.noop(currentState, event);
    }

    const transition = allowedTransitions[0];
    const nextStateName = getTransitionTarget(transition);
    const nextState =
      this.machineDefinition.states[nextStateName || currentState];

    if (!nextState) {
      if (isDevelopment()) {
        throw new Error(`State "${nextStateName}" not found`);
      }

      return this.noop(currentState, event);
    }

    const actions = this.getExecutableActionsForTransition(transition);

    this.inspector.add({
      type: 'fsm.transition.take',
      description: 'Transitioning to a new state',
      data: {
        nextState: nextStateName || currentState,
        previousState: currentState,
        isService: isServiceNode(nextState),
      },
      event,
    });

    if (isServiceNode(nextState)) {
      return this.transitionForService(
        currentState,
        nextState as MachineServiceNode<TContext, TEvent>,
        event,
        actions,
        nextStateName
      );
    }

    return {
      state: nextStateName || currentState,
      context: this.context,
      actions,
      event,
    };
  }

  transitionForEventless(
    currentState: MachineState
  ): MachineTransitionData<TContext, TEvent> {
    const state = this.machineDefinition.states[currentState];
    const event: InternalEvent = { type: EVENTLESS_TRANSITION_ACTION_NAME };
    if (!isEventlessTransition(state)) {
      return this.noop(currentState, event);
    }

    const transitions = (
      state as MachineStateEventlessTransition<TContext, TEvent>
    ).always;
    const allowedTransitions = makeArray(transitions).filter((t) => {
      return this.transitionGuardCheck(t, this.context, event);
    });

    if (allowedTransitions.length === 0) {
      this.inspector.add({
        type: 'fsm.transition.skip',
        description: 'Eventless transition has no allowed transitions',
        event: {
          type: EVENTLESS_TRANSITION_ACTION_NAME,
        },
      });

      return this.noop(currentState, event);
    }

    const transition = allowedTransitions[0];
    const nextStateName = getTransitionTarget(transition);
    const actions = this.getExecutableActionsForTransition(transition);

    this.inspector.add({
      type: 'fsm.transition.take',
      description: 'Eventless transition',
      data: {
        nextState: nextStateName,
        previousState: currentState,
      },
      event: {
        type: EVENTLESS_TRANSITION_ACTION_NAME,
      },
    });

    return {
      state: nextStateName || currentState,
      context: this.context,
      actions,
      event,
    };
  }

  transitionForService(
    currentState: MachineState,
    nextState: MachineServiceNode<TContext, TEvent>,
    event: MachineEvent<TEvent>,
    transitionActions: MachineActionExec<TContext, TEvent>[],
    nextStateName?: MachineState
  ): MachineTransitionData<TContext, TEvent> {
    const stateNode = nextState! as MachineServiceNode<TContext, TEvent>;
    const service = getServiceByName(
      this.machineDefinition,
      stateNode.invoke.src
    );

    const execService = async () => {
      try {
        const result = await service(this.context, event);
        const newEvent: InternalEvent = {
          type: DONE_TRANSITION_ACTION_NAME,
          data: result,
        };

        this.inspector.add({
          type: 'fsm.service.resolve',
          description: 'Service completed successfully',
          data: { state: currentState },
          event: newEvent,
        });

        if (stateNode.invoke.onDone) {
          const allowedTransitions = makeArray(stateNode.invoke.onDone).filter(
            (t) => {
              return this.transitionGuardCheck(t, this.context, newEvent);
            }
          );

          if (allowedTransitions.length === 0) {
            this.inspector.add({
              type: 'fsm.transition.skip',
              description: 'Service transition has no allowed transitions',
              event: newEvent,
            });

            return this.noop(currentState, newEvent);
          }

          const transition = allowedTransitions[0];
          const latestState = getTransitionTarget(transition);
          const actions = this.getExecutableActionsForTransition(transition);

          this.inspector.add({
            type: 'fsm.transition.take',
            description: 'Service transition',
            data: {
              nextState: latestState || currentState,
              previousState: currentState,
            },
            event: newEvent,
          });

          return {
            state: latestState || currentState,
            actions,
            event: newEvent,
            context: this.context,
          };
        } else {
          this.inspector.add({
            type: 'fsm.transition.skip',
            description: 'Service transition has no defined transitions',
            event: newEvent,
          });

          return this.noop(currentState, newEvent);
        }
      } catch (error) {
        const newEvent: InternalEvent = {
          type: ERROR_TRANSITION_ACTION_NAME,
          data: error,
        };

        this.inspector.add({
          type: 'fsm.service.error',
          error,
          data: { state: currentState },
          event: newEvent,
        });

        if (stateNode.invoke.onError) {
          const allowedTransitions = makeArray(stateNode.invoke.onError).filter(
            (t) => {
              return this.transitionGuardCheck(t, this.context, newEvent);
            }
          );

          if (allowedTransitions.length === 0) {
            this.inspector.add({
              type: 'fsm.transition.skip',
              description:
                'Service error transition has no allowed transitions',
              event: newEvent,
            });

            return this.noop(currentState, newEvent);
          }

          const transition = allowedTransitions[0];
          const latestState = getTransitionTarget(transition);
          const actions = this.getExecutableActionsForTransition(transition);

          this.inspector.add({
            type: 'fsm.transition.take',
            description: 'Service error transition',
            data: {
              nextState: latestState || currentState,
              previousState: currentState,
            },
            event: {
              type: ERROR_TRANSITION_ACTION_NAME,
            },
          });

          return {
            state: latestState || currentState,
            actions,
            event: newEvent,
            context: this.context,
          };
        } else {
          this.inspector.add({
            type: 'fsm.transition.skip',
            description: 'Service error transition has no defined transitions',
            event: newEvent,
          });

          return this.noop(currentState, newEvent);
        }
      }
    };

    return {
      state: nextStateName || currentState,
      exec: execService,
      event,
      context: this.context,
      actions: transitionActions,
    };
  }

  getExecutableActionsForTransition(
    transition: MachineTransition<TContext, TEvent>
  ) {
    const actions: string[] = getTransitionActions(transition);
    return actions.map((action) => {
      return {
        type: action,
        exec: (context: TContext, event: MachineEvent<TEvent>) => {
          const fn = getActionByName(this.machineDefinition, action);

          try {
            const actionResolveEntry: any = {
              type: 'fsm.action.resolve',
              description: `Executing action ${action}`,
              data: { action },
            };

            // Only include event for non-internal events
            if (!isInternalEvent(event)) {
              actionResolveEntry.event = event;
            }

            this.inspector.add(actionResolveEntry);

            return fn(context, event);
          } catch (error) {
            const actionErrorEntry: any = {
              type: 'fsm.action.error',
              error,
              data: { action },
            };

            // Only include event for non-internal events
            if (!isInternalEvent(event)) {
              actionErrorEntry.event = event;
            }

            this.inspector.add(actionErrorEntry);

            console.error(`Error executing action: ${action}`);
            console.error(error);
          }
        },
      };
    });
  }

  hasTag(state: MachineState, tag: string) {
    const stateNode = this.machineDefinition.states[state];

    if (!stateNode) {
      this.inspector.add({
        type: 'fsm.state.missing',
        description: 'Could not find state when checking for tag',
        data: { tag, state },
      });

      console.error(`Could not find tag for missing state: ${state}`);

      return false;
    }
    const tags = stateNode.tags || [];

    return tags.includes(tag);
  }

  private isInitializing() {
    return this.status === 'initializing';
  }

  private isRunning() {
    return this.status === 'running';
  }

  private isStopped() {
    return this.status === 'stopped';
  }

  private startRunning() {
    this.status = 'running';
    this.inspector.start();
  }

  private stopRunning() {
    this.status = 'stopped';
    this.inspector.stop();
  }

  private transitionGuardCheck(
    transition: MachineTransition<TContext, TEvent>,
    context: TContext,
    event: MachineEvent<TEvent>
  ) {
    const guardName = getTransitionCondition(transition);

    if (!guardName) {
      return true;
    }

    const guardFn = this.guards[guardName];

    if (!guardName || !guardFn) {
      this.inspector.add({
        type: 'fsm.guard.error',
        description: `No guard found for ${guardName}`,
        event,
      });

      return true;
    }

    const result = guardFn(context, event);

    if (result) {
      this.inspector.add({
        type: 'fsm.guard.take',
        description: `Guard ${guardName} passed`,
        event,
      });
    } else {
      this.inspector.add({
        type: 'fsm.guard.skip',
        description: `Guard ${guardName} failed`,
        event,
      });
    }

    return result;
  }
}
